import { useEffect, useState } from 'react'

interface UseScrollSpyOptions {
  ids: string[]
  rootMargin?: string
  threshold?: number | number[]
  activeFallbackId?: string
}

export function useScrollSpy({
  ids,
  rootMargin = '0px 0px -55% 0px',
  threshold = [0, 0.25, 0.5, 0.75, 1],
  activeFallbackId,
}: UseScrollSpyOptions): string | null {
  const [activeId, setActiveId] = useState<string | null>(activeFallbackId ?? null)

  useEffect(() => {
    const elements = ids.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => Boolean(el))

    if (!elements.length) return

    const visibilityMap = new Map<string, number>()

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id
          ;(visibilityMap as Map<string, number>).set(id, entry.intersectionRatio)
        })

        // Pick the most visible section
        let topId: string | null = null
        let max = 0
        visibilityMap.forEach((val, key) => {
          if (val > max) {
            max = val
            topId = key
          }
        })

        if (topId && topId !== activeId) {
          setActiveId(topId)
        }
      },
      { root: null, rootMargin, threshold },
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [ids, rootMargin, threshold, activeId])

  return activeId
}
